/**
 * src/hooks/useAdminPanel.js
 * React Query v5 hooks para las vistas de detalle del panel de control.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getActividad, getActividadDia, getFamilias, getSchedulerDetalle, getAlertas,
  getEmbudo, getUso, getUsoAgregado, getUsoCliente, getUsoHorarios,
  getCifrasLanding,
  getEjecuciones,
  getFichaCliente,
  getEstado,
  getFrescura,
  postEstadoRechequear,
  postResolverAlertaEstado,
} from '../api/adminPanel'

export function useActividad(dias) {
  return useQuery({
    queryKey: ['panel', 'actividad', dias],
    queryFn: () => getActividad(dias),
    refetchInterval: 60_000,
  })
}

export function useActividadDia(fecha) {
  return useQuery({
    queryKey: ['panel', 'actividad', 'dia', fecha],
    queryFn: () => getActividadDia(fecha),
    enabled: !!fecha,
  })
}

export function useFamilias() {
  return useQuery({
    queryKey: ['panel', 'familias'],
    queryFn: getFamilias,
  })
}

export function useSchedulerDetalle() {
  return useQuery({
    queryKey: ['panel', 'scheduler'],
    queryFn: getSchedulerDetalle,
    refetchInterval: 60_000,
  })
}

export function useAlertas(params) {
  return useQuery({
    queryKey: ['panel', 'alertas', params],
    queryFn: () => getAlertas(params),
    refetchInterval: 60_000,
  })
}

export function useEmbudo(dias) {
  return useQuery({
    queryKey: ['panel', 'embudo', dias],
    queryFn: () => getEmbudo(dias),
    refetchInterval: 120_000,
  })
}

export function useUso(dias) {
  return useQuery({
    queryKey: ['panel', 'uso', dias],
    queryFn: () => getUso(dias),
    refetchInterval: 120_000,
  })
}

export function useUsoAgregado(dias) {
  return useQuery({
    queryKey: ['panel', 'uso', 'agregado', dias],
    queryFn: () => getUsoAgregado(dias),
    refetchInterval: 120_000,
  })
}

export function useUsoCliente(clienteId, dias) {
  return useQuery({
    queryKey: ['panel', 'uso', 'cliente', clienteId, dias],
    queryFn: () => getUsoCliente(clienteId, dias),
    enabled: !!clienteId,
  })
}

export function useUsoHorarios(dias) {
  return useQuery({
    queryKey: ['panel', 'uso', 'horarios', dias],
    queryFn: () => getUsoHorarios(dias),
    refetchInterval: 120_000,
  })
}

// Se recalcula recorriendo los JSON de cada tenant: no es barato y no cambia
// por minutos. Basta con refrescarlo al entrar y cada media hora.
export function useCifrasLanding(diasOc = 30) {
  return useQuery({
    queryKey: ['panel', 'cifras-landing', diasOc],
    queryFn: () => getCifrasLanding(diasOc),
    staleTime: 300_000,
    refetchInterval: 1_800_000,
  })
}

export function useEstado() {
  return useQuery({
    queryKey: ['panel', 'estado'],
    queryFn: getEstado,
    // La pantalla se mira para saber si algo se rompió: media hora de datos
    // viejos es media hora creyendo que todo va bien.
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

// La tabla de frescura va en su propia query, no dentro del parte: así una
// avería suya deja un hueco con su mensaje en vez de vaciar la pantalla entera.
// Se refresca al mismo ritmo que el parte, porque contesta a la misma pregunta.
export function useFrescura() {
  return useQuery({
    queryKey: ['panel', 'frescura'],
    queryFn: getFrescura,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

// «Actualizar» y «Marcar resuelta» tocan el estado en el servidor: al volver,
// se invalida la query para que el parte se repinte con lo que quedó vigente.
// Se invalida también en error a propósito — si el cierre devolvió 404 es que
// la alerta ya no estaba vigente, y el repintado la hace desaparecer igual.
export function useRechequearEstado() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: postEstadoRechequear,
    onSettled: () => qc.invalidateQueries({ queryKey: ['panel', 'estado'] }),
  })
}

export function useResolverAlertaEstado() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: postResolverAlertaEstado,
    // El cierre se usa desde el parte Y desde el archivo (28-08): se repintan
    // los dos, esté donde esté el que lo pulsó.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['panel', 'estado'] })
      qc.invalidateQueries({ queryKey: ['panel', 'alertas'] })
    },
  })
}

export function useEjecuciones({ tenant, tipo, soloError, dias = 7 } = {}) {
  return useQuery({
    // El filtro entra en la clave: sin él, React Query serviría los datos de
    // otro tenant al cambiar de selección.
    queryKey: ['panel', 'ejecuciones', tenant ?? 'todos', tipo ?? 'todo', soloError, dias],
    queryFn: () => getEjecuciones({ tenant, tipo, soloError, dias }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

export function useFichaCliente(clienteId) {
  return useQuery({
    queryKey: ['panel', 'ficha', clienteId],
    queryFn: () => getFichaCliente(clienteId),
    enabled: !!clienteId,
    staleTime: 30_000,
  })
}
